import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BrutalButton, BrutalCard } from '@/components/brutal';
import { Sparkles, Send, SkipForward } from 'lucide-react';
import { reflectionAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Ungraded character-reflection prompt shown after a chapter. One question
 * per chapter pick. Not a right-or-wrong thing — the child writes freely,
 * we save it, and the parent sees a rolled-up view in the Integrity tab.
 *
 * If the student skips, no harm done. If they answer, the question gets
 * tagged with its axis (honesty/patience/teamwork/empathy/etc) and tallied
 * on their CharacterProfile.
 */
const ReflectionQuestionModal = ({ studentId, narrativeId, onClose }) => {
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reflection-next', studentId],
    queryFn: async () => (await reflectionAPI.next(studentId, narrativeId)).data,
    enabled: !!studentId,
  });

  const question = data?.question;

  const handleSubmit = async () => {
    if (!answer.trim() || !question) return;
    setSubmitting(true);
    try {
      await reflectionAPI.submit({
        student_id: studentId,
        question_id: question.id,
        answer_text: answer.trim(),
        narrative_id: narrativeId,
      });
      toast.success('Thanks for sharing!');
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <BrutalCard className="bg-white"><p className="text-lg font-bold">Loading…</p></BrutalCard>
      </div>
    );
  }

  // If no question available (bank exhausted or other), silently close
  if (error || !question) {
    onClose(false);
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 overflow-y-auto">
      <BrutalCard shadow="xl" className="w-full max-w-xl bg-white my-auto">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-violet-500 text-white w-12 h-12 flex items-center justify-center border-4 border-black flex-shrink-0">
            <Sparkles size={24} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-violet-600 tracking-widest">Think About It</p>
            <h2 className="text-lg sm:text-xl font-black uppercase">Just for you — no right or wrong</h2>
          </div>
        </div>

        <p className="text-base sm:text-lg font-medium mb-4 leading-relaxed">
          {question.prompt}
        </p>

        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Type your thoughts here… There's no right or wrong answer."
          rows={5}
          className="w-full px-3 py-3 border-4 border-black font-medium resize-none focus:outline-none focus:ring-4 focus:ring-violet-300 mb-4"
          data-testid="reflection-answer"
        />

        <div className="flex gap-2">
          <BrutalButton
            variant="violet"
            size="lg"
            onClick={handleSubmit}
            disabled={!answer.trim() || submitting}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <Send size={18} /> {submitting ? 'Saving…' : 'Share'}
          </BrutalButton>
          <BrutalButton
            variant="ghost"
            size="lg"
            onClick={() => onClose(false)}
            className="flex items-center gap-2"
          >
            <SkipForward size={18} /> Skip
          </BrutalButton>
        </div>

        <p className="text-xs text-gray-400 mt-3 text-center">
          Your answer is saved for your parent or teacher to read. It's not graded.
        </p>
      </BrutalCard>
    </div>
  );
};

export default ReflectionQuestionModal;
